import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import WatchLiveButton from '@/components/WatchLiveButton';
import {
  getTournament,
  getMatchesByTournament,
  getTeams,
  getTournamentStats,
  getPointsTable,
  getPlayerStars,
  getTournaments,
  getTournamentParticipants,
  calculateDuration,
  getClubLogoUrl,
  getBroadcastSettings,
  slugifyPlayerName,
  supabase,
} from '@/lib/supabase';
import { TeamLogo } from '@/components/TeamLogo';
import { PointsTableRow, PlayerStarRow } from '@/lib/supabase';
import { resolveServingInfo } from '@/lib/matchUtils';
import { capture, capturePageView } from '@/lib/posthog';

// Safety-net poll interval — realtime subscriptions handle all live updates.
// This only fires to recover from a dropped WebSocket connection.
const FALLBACK_POLL_INTERVAL = 60000;

// Debounce window: a single rally writes to both `matches` and `sets` in quick
// succession. Coalescing those two realtime events into one refresh avoids a
// double fetch on every scored point.
const REALTIME_DEBOUNCE_MS = 150;

type Tab = 'live' | 'points' | 'players' | 'tournament';
type PlayerCategory = 'mvp' | 'thrower' | 'acer' | 'catcher';

interface TeamInfo {
  id: string;
  name: string;
  shortName: string;
  color: string;
  logoUrl?: string | null;
  players?: { id: string; name: string; teamId: string }[];
}

interface MatchInfo {
  id: string;
  matchNumber: number;
  broadcastId: string | null;
  matchType: string;
  matchLabel?: string | null;
  status: string;
  date: string | null;
  matchTime?: string | null;
  teamAId: string;
  teamBId: string;
  teamA: TeamInfo | null;
  teamB: TeamInfo | null;
  winnerId: string | null;
  startedAt: string | null;
  endedAt: string | null;
  sets: { set_number: number; score_a: number; score_b: number; winner_id: string | null }[];
  currentServingTeamId?: string | null;
  currentServerId?: string | null;
  liveStreamUrl?: string | null;
}

function formatMatchTime(time: string | null): string {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 || 12;
  return `${display}:${m} ${ampm}`;
}

function formatMatchDate(date: string | null): string {
  if (!date) return '';
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatElapsed(startedAt: string | null): string {
  if (!startedAt) return '00:00';
  const diff = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function PublicTournamentCentre() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showAll = searchParams.get('showAll') === 'true';
  const [tournaments, setTournaments] = useState<{ id: string; name: string; date: string; venue: string | null }[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(tournamentId ?? null);
  const [tournamentName, setTournamentName] = useState<string>('');
  const [teams, setTeams] = useState<TeamInfo[]>([]);
  const [matches, setMatches] = useState<MatchInfo[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, live: 0, pending: 0, scheduled: 0 });
  const [pointsRows, setPointsRows] = useState<PointsTableRow[]>([]);
  const [playerRows, setPlayerRows] = useState<PlayerStarRow[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>('live');
  const [matchesSubTab, setMatchesSubTab] = useState<'live' | 'scheduled' | 'completed'>('live');
  const [playerCategory, setPlayerCategory] = useState<PlayerCategory>('mvp');
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [playerSearch, setPlayerSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [clubLogo, setClubLogo] = useState<string | null>(null);
  const livePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Stable refs so realtime/poll closures always call the latest callback version
  // without needing to re-create the channel/interval on every render.
  const refreshLiveDataRef = useRef<(tid: string) => Promise<void>>(async () => {});
  const refreshStatsDataRef = useRef<(tid: string) => Promise<void>>(async () => {});
  // Debounce timer: coalesces rapid back-to-back realtime events (e.g. matches +
  // sets both updating on a single rally) into one refresh call.
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the IDs of currently live/active matches so the sets channel can be
  // rebuilt with the correct filter whenever the live match list changes.
  const liveMatchIdsRef = useRef<string[]>([]);

  // Load club logo
  useEffect(() => {
    getClubLogoUrl().then(url => setClubLogo(url)).catch(() => setClubLogo(null));
  }, []);

  // Load tournaments list — use admin-configured default if no tournamentId in URL
  useEffect(() => {
    Promise.all([getTournaments(), getBroadcastSettings().catch(() => null)]).then(([data, settings]) => {
      setTournaments(data);
      if (!selectedTournamentId && data.length > 0) {
        const defaultId = settings?.defaultTournamentId;
        const pick = (defaultId && data.find(t => t.id === defaultId)) ? defaultId : data[0].id;
        setSelectedTournamentId(pick);
      }
    }).catch(console.error);
  }, []);

  // Update URL when tournament changes
  useEffect(() => {
    if (selectedTournamentId && selectedTournamentId !== tournamentId) {
      navigate(`/centre/${selectedTournamentId}`, { replace: true });
    }
  }, [selectedTournamentId, tournamentId, navigate]);

  // Format teams helper
  const formatTeamsMap = useCallback((data: any[]): TeamInfo[] =>
    data.map(t => ({
      id: t.id,
      name: t.name,
      shortName: t.short_name ?? t.shortName,
      color: t.color,
      logoUrl: t.logo_url ?? t.logoUrl,
      players: (t.players || []).map((p: any) => ({ id: p.id, name: p.name, teamId: p.team_id })),
    })), []);

  // Refresh match + team data (called by realtime and fallback poll)
  const refreshLiveData = useCallback(async (tid: string) => {
    try {
      let matchesData: any = null;
      let teamsData: any = null;
      let participantsData: any = null;
      try { matchesData = await getMatchesByTournament(tid); } catch { /* ignore */ }
      try { teamsData = await getTeams(); } catch { /* ignore */ }
      try { participantsData = await getTournamentParticipants(tid); } catch { /* ignore */ }

      const teamsMap = teamsData ? formatTeamsMap(teamsData) : [];
      const participatingTeams = participantsData ? teamsMap.filter((t: TeamInfo) => participantsData.includes(t.id)) : [];
      setTeams(participatingTeams.length > 0 ? participatingTeams : teamsMap);

      const formattedMatches: MatchInfo[] = matchesData ? matchesData.map((m: any) => ({
        id: m.id,
        matchNumber: m.match_number,
        broadcastId: m.broadcast_id ?? null,
        matchType: m.match_type,
        matchLabel: m.match_label ?? null,
        status: m.status,
        date: m.date ?? null,
        matchTime: m.match_time ?? null,
        liveStreamUrl: m.live_stream_url ?? null,
        teamAId: m.team_a_id,
        teamBId: m.team_b_id,
        teamA: teamsMap.find((t: TeamInfo) => t.id === m.team_a_id) ?? null,
        teamB: teamsMap.find((t: TeamInfo) => t.id === m.team_b_id) ?? null,
        winnerId: m.winner_id,
        startedAt: m.started_at,
        endedAt: m.ended_at,
        currentServingTeamId: m.current_serving_team_id ?? null,
        currentServerId: m.current_server_id ?? null,
        sets: (m.sets || []).map((s: any) => ({
          set_number: s.set_number,
          score_a: s.score_a,
          score_b: s.score_b,
          winner_id: s.winner_id,
        })),
      })) : [];
      setMatches(formattedMatches);
    } catch (err) {
      console.error('[refreshLiveData] error:', err);
    }
  }, [formatTeamsMap]);

  // Refresh stats-only data (points table, player stars) — cheaper, runs less often
  const refreshStatsData = useCallback(async (tid: string) => {
    try {
      let statsData: any = null;
      let pts: any = null;
      let players: any = null;
      try { statsData = await getTournamentStats(tid); } catch { /* ignore */ }
      try { pts = await getPointsTable(tid); } catch { /* ignore */ }
      try { players = await getPlayerStars(tid); } catch { /* ignore */ }
      if (statsData) setStats(statsData);
      if (pts) setPointsRows(pts);
      if (players) setPlayerRows(players);
    } catch (err) {
      console.error('[refreshStatsData] error:', err);
    }
  }, []);

  // Initial full load (tournament name + live + stats)
  useEffect(() => {
    if (!selectedTournamentId) return;
    const init = async () => {
      setLoading(true);
      try {
        let tournamentData: any = null;
        try { tournamentData = await getTournament(selectedTournamentId); } catch { /* ignore */ }
        if (tournamentData) setTournamentName(tournamentData.name);
        await Promise.all([
          refreshLiveData(selectedTournamentId),
          refreshStatsData(selectedTournamentId),
        ]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [selectedTournamentId, refreshLiveData, refreshStatsData]);

  // Track page view whenever selected tournament changes
  useEffect(() => {
    if (!selectedTournamentId) return;
    capturePageView();
    capture('tournament_viewed', {
      tournament_id: selectedTournamentId,
      tournament_name: tournamentName,
    });
  }, [selectedTournamentId]);

  // Keep refs in sync so realtime/poll closures never call stale callbacks
  useEffect(() => { refreshLiveDataRef.current = refreshLiveData; }, [refreshLiveData]);
  useEffect(() => { refreshStatsDataRef.current = refreshStatsData; }, [refreshStatsData]);

  // Keep liveMatchIdsRef in sync whenever the matches list changes so the sets
  // channel (rebuilt below) always filters on the current live match IDs.
  useEffect(() => {
    liveMatchIdsRef.current = matches
      .filter(m => m.status === 'live' || m.status === 'set-won' || m.status === 'first-server')
      .map(m => m.id);
  }, [matches]);

  // Realtime subscriptions + safety-net fallback polling.
  //
  // Two channels:
  //   1. `matches`  — fires on status/serving changes (every rally, set win, etc.)
  //   2. `sets`     — fires on score updates (score_a / score_b written per rally)
  //
  // Both channels feed into the same debounced refresh so back-to-back writes
  // from a single rally (matches row + sets row updated together) produce exactly
  // one re-fetch instead of two.
  useEffect(() => {
    if (!selectedTournamentId) return;

    const scheduleRefresh = () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      realtimeDebounceRef.current = setTimeout(() => {
        refreshLiveDataRef.current(selectedTournamentId);
      }, REALTIME_DEBOUNCE_MS);
    };

    // Channel 1: match-level changes (status, serving team/server, winner, etc.)
    const matchesChannel = supabase
      .channel(`tb-tournament-matches-${selectedTournamentId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'matches', filter: `tournament_id=eq.${selectedTournamentId}` },
        scheduleRefresh,
      )
      .subscribe();

    // Channel 2: set-score changes — filter by the live match IDs known at
    // subscription time. The channel is rebuilt (this effect re-runs) whenever
    // selectedTournamentId changes; for mid-match set advances the matches channel
    // fires first and its refresh will pick up the new set row anyway.
    const liveIds = liveMatchIdsRef.current;
    const setsChannel = liveIds.length > 0
      ? supabase
          .channel(`tb-tournament-sets-${selectedTournamentId}`)
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'sets', filter: `match_id=in.(${liveIds.join(',')})` },
            scheduleRefresh,
          )
          .subscribe()
      : null;

    // Safety-net poll — only exists to recover from a dropped WebSocket connection.
    // Under normal operation the two realtime channels handle all updates.
    livePollRef.current = setInterval(() => {
      if (!document.hidden) refreshLiveDataRef.current(selectedTournamentId);
    }, FALLBACK_POLL_INTERVAL);

    statsPollRef.current = setInterval(() => {
      if (!document.hidden) refreshStatsDataRef.current(selectedTournamentId);
    }, FALLBACK_POLL_INTERVAL);

    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      supabase.removeChannel(matchesChannel);
      if (setsChannel) supabase.removeChannel(setsChannel);
      if (livePollRef.current) clearInterval(livePollRef.current);
      if (statsPollRef.current) clearInterval(statsPollRef.current);
    };
  }, [selectedTournamentId]);

  // Timer tick
  useEffect(() => {
    timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Document title
  useEffect(() => {
    document.title = tournamentName ? `Throwball - ${tournamentName}` : 'Throwball Scorer';
    return () => { document.title = 'Throwball Scorer'; };
  }, [tournamentName]);

  // Derived state
  const liveMatches = useMemo(() => matches.filter(m => m.status === 'live' || m.status === 'set-won' || m.status === 'first-server'), [matches]);
  const scheduledMatches = useMemo(() => matches.filter(m => m.status === 'scheduled'), [matches]);
  const completedMatches = useMemo(() => matches.filter(m => m.status === 'match-won'), [matches]);

  // Auto-select the best default sub-tab whenever match data loads or changes.
  // Priority: live → upcoming → completed.
  useEffect(() => {
    if (liveMatches.length > 0) setMatchesSubTab('live');
    else if (scheduledMatches.length > 0) setMatchesSubTab('scheduled');
    else setMatchesSubTab('completed');
  }, [liveMatches.length, scheduledMatches.length]);

  // Filter players by search + team, then sort per category
  const filteredPlayers = useMemo(() => {
    let filtered = [...playerRows];
    if (playerSearch) {
      const search = playerSearch.toLowerCase();
      filtered = filtered.filter(p => p.playerName.toLowerCase().includes(search));
    }
    if (teamFilter !== 'all') {
      filtered = filtered.filter(p => p.teamId === teamFilter);
    }
    return filtered;
  }, [playerRows, playerSearch, teamFilter]);

  const totalRallies = useMemo(() => {
    return matches.reduce((sum, m) => {
      return sum + m.sets.reduce((sSum, s) => {
        return sSum + s.score_a + s.score_b;
      }, 0);
    }, 0);
  }, [matches]);

  const totalSets = useMemo(() => matches.reduce((sum, m) => sum + (m.sets?.length || 0), 0), [matches]);
  const totalPoints = useMemo(() => matches.reduce((sum, m) => sum + m.sets.reduce((sSum, s) => sSum + s.score_a + s.score_b, 0), 0), [matches]);

  const tournamentDuration = useMemo(() => {
    const startedMatches = matches.filter(m => m.startedAt);
    const endedMatches = matches.filter(m => m.endedAt);
    if (startedMatches.length === 0) return null;
    const firstStart = startedMatches.reduce((min, m) => m.startedAt! < min ? m.startedAt! : min, startedMatches[0].startedAt!);
    const lastEnd = endedMatches.length > 0 ? endedMatches.reduce((max, m) => m.endedAt! > max ? m.endedAt! : max, endedMatches[0].endedAt!) : null;
    return calculateDuration(firstStart, lastEnd);
  }, [matches]);

  void tick;

  // Tournament selector
  if (!selectedTournamentId && tournaments.length > 1) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-20 bg-surface/95 backdrop-blur-sm border-b border-border">
          <div className="max-w-2xl mx-auto px-4 h-14 flex items-center">
            <h1 className="text-lg font-bold text-text-primary">Select Tournament</h1>
          </div>
        </header>
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">
          {tournaments.map(t => (
            <button
              key={t.id}
              onClick={() => { setSelectedTournamentId(t.id); setTournamentName(t.name); }}
              className="w-full p-4 rounded-xl text-left bg-surface border border-border shadow-sm transition-all active:scale-[0.99] hover:shadow-md"
            >
              <p className="font-bold text-text-primary">{t.name}</p>
              <p className="text-sm text-text-muted">{new Date(t.date).toLocaleDateString()}{t.venue ? ` — ${t.venue}` : ''}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (loading && matches.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Sticky Header + Tabs */}
      <div className="sticky top-0 z-40">

        {/* Header */}
        <div className="bg-surface border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-2">
            <div className="flex items-center gap-2">
              {clubLogo && (
                <img src={clubLogo} alt="Club" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
              )}
              {showAll && tournaments.length > 1 ? (
                <select
                  value={selectedTournamentId || ''}
                  onChange={e => { setSelectedTournamentId(e.target.value); }}
                  className="flex-1 min-w-0 text-sm bg-surface-raised border border-border rounded-lg px-2 py-1.5 font-semibold text-text-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {tournaments.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              ) : (
                <h1 className="flex-1 min-w-0 text-sm font-bold text-text-primary truncate">{tournamentName || 'Tournament'}</h1>
              )}
              <a
                href={`${window.location.origin.replace(/throwball/i, 'volleyball')}/live`}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-info/10 text-info hover:bg-info/20 transition-colors font-bold text-xs leading-none whitespace-nowrap flex-shrink-0"
                title="View Volleyball live scores"
              >
                Volleyball
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-surface border-b border-border">
          <nav className="max-w-4xl mx-auto flex">
            <TabButton icon="🏐" label="Matches" active={activeTab === 'live'} onClick={() => { setActiveTab('live'); capture('public_tab_changed', { tab: 'live', tournament_id: selectedTournamentId }); }} />
            <TabButton icon="🏆" label="Table" active={activeTab === 'points'} onClick={() => { setActiveTab('points'); capture('public_tab_changed', { tab: 'standings', tournament_id: selectedTournamentId }); }} />
            <TabButton icon="🌟" label="Stars" active={activeTab === 'players'} onClick={() => { setActiveTab('players'); capture('public_tab_changed', { tab: 'players', tournament_id: selectedTournamentId }); }} />
            <TabButton
              icon={(
                <svg className="w-4 h-4 sm:w-[18px] sm:h-[18px] inline-block" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M18 17V9" />
                  <path d="M13 17V5" />
                  <path d="M8 17v-4" />
                </svg>
              )}
              label="Stats"
              active={activeTab === 'tournament'}
              onClick={() => { setActiveTab('tournament'); capture('public_tab_changed', { tab: 'stats', tournament_id: selectedTournamentId }); }}
            />
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {activeTab === 'live' && (
            <LiveMatchesTab
              liveMatches={liveMatches}
              scheduledMatches={scheduledMatches}
              completedMatches={completedMatches}
              tournamentName={tournamentName}
              subTab={matchesSubTab}
              setSubTab={setMatchesSubTab}
              navigate={navigate}
            />
          )}
          {activeTab === 'points' && (
            <PointsTableTab rows={pointsRows} />
          )}
          {activeTab === 'players' && (
            <PlayerStatsTab
              players={filteredPlayers}
              teams={teams}
              playerSearch={playerSearch}
              setPlayerSearch={setPlayerSearch}
              teamFilter={teamFilter}
              setTeamFilter={setTeamFilter}
              playerCategory={playerCategory}
              setPlayerCategory={setPlayerCategory}
              navigate={navigate}
            />
          )}
          {activeTab === 'tournament' && (
            <TournamentStatsTab
              stats={stats}
              totalRallies={totalRallies}
              totalSets={totalSets}
              totalPoints={totalPoints}
              duration={tournamentDuration}
              matches={matches}
              teams={teams}
              pointsRows={pointsRows}
              playerRows={playerRows}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Tab Button — equal-width column, icon + label stacked (matches vb-app tab bar)
function TabButton({ icon, label, active, onClick }: { icon: ReactNode; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center gap-1.5 px-1 py-2 sm:py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wide transition-colors border-b-2 ${
        active
          ? 'text-info border-info'
          : 'text-text-secondary border-transparent hover:text-text-primary'
      }`}
    >
      <span className="text-sm sm:text-base leading-none">{icon}</span>
      <span className="leading-none">{label}</span>
    </button>
  );
}

type MatchesSubTab = 'live' | 'scheduled' | 'completed';

// Matches Tab — Live / Upcoming / Completed sub-tabs
function LiveMatchesTab({
  liveMatches,
  scheduledMatches,
  completedMatches,
  tournamentName,
  subTab,
  setSubTab,
  navigate,
}: {
  liveMatches: MatchInfo[];
  scheduledMatches: MatchInfo[];
  completedMatches: MatchInfo[];
  tournamentName: string;
  subTab: MatchesSubTab;
  setSubTab: (tab: MatchesSubTab) => void;
  navigate: (path: string) => void;
}) {
  return (
    <div className="space-y-3">
      {/* Sub-tabs */}
      <div className="flex gap-1.5">
        <MatchesSubTabButton label="Live" count={liveMatches.length} active={subTab === 'live'} onClick={() => setSubTab('live')} />
        <MatchesSubTabButton label="Upcoming" count={scheduledMatches.length} active={subTab === 'scheduled'} onClick={() => setSubTab('scheduled')} />
        <MatchesSubTabButton label="Completed" count={completedMatches.length} active={subTab === 'completed'} onClick={() => setSubTab('completed')} />
      </div>

      {/* Live Matches */}
      {subTab === 'live' && (
        liveMatches.length > 0 ? (
          <div className="space-y-2">
            {liveMatches.map(match => (
              <LiveMatchCard key={match.id} match={match} onClick={() => { capture('match_viewed', { match_id: match.id, match_type: match.matchType, broadcast_id: match.broadcastId, status: 'live' }); navigate(`/live/${match.broadcastId ?? match.matchNumber}`); }} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">🏐</div>
            <p className="text-text-secondary text-sm">No live matches right now</p>
            <p className="text-text-muted text-xs mt-1">Check Upcoming Matches!</p>
          </div>
        )
      )}

      {/* Upcoming Matches */}
      {subTab === 'scheduled' && (
        scheduledMatches.length > 0 ? (
          <div className="space-y-2">
            {scheduledMatches.map(match => (
              <ScheduledMatchCard key={match.id} match={match} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">🏐</div>
            <p className="text-text-secondary text-sm">No upcoming matches</p>
            <p className="text-text-muted text-xs mt-1">Check Upcoming Matches!</p>
          </div>
        )
      )}

      {/* Completed Matches */}
      {subTab === 'completed' && (
        completedMatches.length > 0 ? (
          <div className="space-y-2">
            {completedMatches.map(match => (
              <CompletedMatchCard key={match.id} match={match} tournamentName={tournamentName} onClick={() => { capture('match_viewed', { match_id: match.id, match_type: match.matchType, broadcast_id: match.broadcastId }); navigate(`/live/${match.broadcastId ?? match.matchNumber}`); }} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-text-secondary text-sm">No completed matches yet</p>
          </div>
        )
      )}
    </div>
  );
}

function MatchesSubTabButton({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-bold transition-colors ${
        active ? 'bg-info text-white' : 'bg-surface border border-border text-text-secondary hover:text-text-primary'
      }`}
    >
      <span>{label}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-surface-raised'}`}>{count}</span>
    </button>
  );
}

function LiveMatchCard({ match, onClick }: { match: MatchInfo; onClick: () => void }) {
  const elapsed = formatElapsed(match.startedAt);
  const isStarting = match.status === 'first-server';

  const completedSets = match.sets.filter(s => s.winner_id !== null);
  const activeSet = match.sets.find(s => s.winner_id === null) ?? match.sets[match.sets.length - 1];
  const currentSetNumber = activeSet?.set_number ?? 1;
  const scoreA = activeSet?.score_a ?? 0;
  const scoreB = activeSet?.score_b ?? 0;
  const setsWonA = completedSets.filter(s => s.winner_id === match.teamAId).length;
  const setsWonB = completedSets.filter(s => s.winner_id === match.teamBId).length;
  const maxSets = match.matchType === 'playoff' ? 5 : 3;

  const { servingTeam, serverName } = resolveServingInfo(
    match.currentServingTeamId,
    match.currentServerId,
    match.teamA,
    match.teamB,
  );

  return (
    <div
      onClick={onClick}
      className="w-full bg-surface border border-border rounded-xl p-4 hover:border-text-muted transition-colors text-left cursor-pointer"
    >
      {/* Match Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold text-text-muted">MATCH #{match.matchNumber}</span>
        <div className="flex items-center gap-2">
          {match.matchType === 'playoff' && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-warning/20 text-warning">PLAYOFF</span>
          )}
          {isStarting ? (
            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-success/20 text-success">
              <span className="text-[10px] font-bold uppercase">STARTING</span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-success/20 text-success">
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span className="text-[10px] font-bold uppercase">LIVE</span>
            </span>
          )}
        </div>
      </div>

      {/* Teams */}
      <div className="flex items-center justify-between gap-4">
        {/* Team A */}
        <div className="flex-1 flex items-center gap-3">
          <div className="flex flex-col items-center gap-1 min-w-[60px]">
            {match.teamA ? (
              <div className="w-12 h-12">
                <TeamLogo team={match.teamA as any} size="lg" />
              </div>
            ) : null}
            <span className="text-xs font-bold truncate max-w-[80px]" style={{ color: match.teamA?.color }}>
              {match.teamA?.shortName ?? '—'}
            </span>
            <div className="flex gap-1 justify-center">
              {Array.from({ length: maxSets }, (_, i) => (
                <span
                  key={i}
                  className={`w-2 h-2 rounded-full ${i < setsWonA ? '' : 'bg-border'}`}
                  style={i < setsWonA ? { backgroundColor: match.teamA?.color } : {}}
                />
              ))}
            </div>
          </div>
          <div className="text-center">
            <p className="font-mono-nums text-4xl font-bold text-text-primary">{scoreA}</p>
          </div>
        </div>

        {/* VS */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <span className="text-xs font-bold text-text-muted">SET {currentSetNumber}</span>
          <span className="text-lg font-bold text-text-muted">VS</span>
          <span className="text-[10px] text-text-muted">{setsWonA} - {setsWonB}</span>
        </div>

        {/* Team B */}
        <div className="flex-1 flex items-center gap-3 flex-row-reverse">
          <div className="flex flex-col items-center gap-1 min-w-[60px]">
            {match.teamB ? (
              <div className="w-12 h-12">
                <TeamLogo team={match.teamB as any} size="lg" />
              </div>
            ) : null}
            <span className="text-xs font-bold truncate max-w-[80px]" style={{ color: match.teamB?.color }}>
              {match.teamB?.shortName ?? '—'}
            </span>
            <div className="flex gap-1 justify-center">
              {Array.from({ length: maxSets }, (_, i) => (
                <span
                  key={i}
                  className={`w-2 h-2 rounded-full ${i < setsWonB ? '' : 'bg-border'}`}
                  style={i < setsWonB ? { backgroundColor: match.teamB?.color } : {}}
                />
              ))}
            </div>
          </div>
          <div className="text-center">
            <p className="font-mono-nums text-4xl font-bold text-text-primary">{scoreB}</p>
          </div>
        </div>
      </div>

      {/* Footer: serving + timer */}
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5">
          {servingTeam ? (
            <>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: servingTeam.color }} />
              <span className="text-text-muted">Serving:</span>
              <span className="font-bold" style={{ color: servingTeam.color }}>{servingTeam.shortName}</span>
              {serverName && <span className="text-text-muted">{serverName}</span>}
            </>
          ) : null}
        </span>
        <span className="font-mono text-text-muted">{elapsed}</span>
      </div>
      {match.liveStreamUrl && (
        <WatchLiveButton url={match.liveStreamUrl} onClick={(e) => e.stopPropagation()} />
      )}
    </div>
  );
}

function ScheduledMatchCard({ match }: { match: MatchInfo }) {
  const isTBDA = !match.teamA || match.teamA.shortName === 'TBD';
  const isTBDB = !match.teamB || match.teamB.shortName === 'TBD';
  return (
    <div className="w-full bg-surface border border-border rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold text-text-secondary">MATCH #{match.matchNumber}</span>
        <div className="flex items-center gap-2">
          {match.matchLabel ? (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-warning/20 text-warning uppercase">{match.matchLabel}</span>
          ) : match.matchType === 'playoff' ? (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-warning/20 text-warning">PLAYOFF</span>
          ) : (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-info/20 text-info">LEAGUE</span>
          )}
          <span className="px-2 py-0.5 rounded bg-surface-raised text-text-secondary text-[10px] font-bold uppercase">
            UPCOMING
          </span>
        </div>
      </div>

      {/* Teams */}
      <div className="flex items-center justify-between gap-4">
        {/* Team A */}
        <div className="flex-1 flex items-center gap-3">
          <div className="flex flex-col items-center gap-1 min-w-[60px]">
            {isTBDA ? (
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-surface-raised border border-border">
                <span className="text-text-muted font-bold text-sm">?</span>
              </div>
            ) : match.teamA ? (
              <div className="w-12 h-12 flex items-center justify-center">
                <TeamLogo team={match.teamA as any} size="lg" />
              </div>
            ) : null}
            <span className={`text-xs font-bold truncate max-w-[80px] ${isTBDA ? 'text-text-muted italic' : ''}`} style={isTBDA ? undefined : { color: match.teamA?.color }}>
              {isTBDA ? 'TBD' : match.teamA?.shortName}
            </span>
          </div>
        </div>

        {/* VS */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <span className="text-lg font-bold text-text-secondary">VS</span>
        </div>

        {/* Team B */}
        <div className="flex-1 flex items-center gap-3 flex-row-reverse">
          <div className="flex flex-col items-center gap-1 min-w-[60px]">
            {isTBDB ? (
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-surface-raised border border-border">
                <span className="text-text-muted font-bold text-sm">?</span>
              </div>
            ) : match.teamB ? (
              <div className="w-12 h-12 flex items-center justify-center">
                <TeamLogo team={match.teamB as any} size="lg" />
              </div>
            ) : null}
            <span className={`text-xs font-bold truncate max-w-[80px] ${isTBDB ? 'text-text-muted italic' : ''}`} style={isTBDB ? undefined : { color: match.teamB?.color }}>
              {isTBDB ? 'TBD' : match.teamB?.shortName}
            </span>
          </div>
        </div>
      </div>

      {/* Date / Time */}
      {(match.date || match.matchTime) && (
        <div className="mt-3 pt-3 border-t border-border flex items-center justify-center text-xs">
          <span className="text-text-secondary font-medium">
            {formatMatchDate(match.date)}{match.matchTime ? ` · ${formatMatchTime(match.matchTime)}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}

function CompletedMatchCard({ match, onClick, tournamentName }: { match: MatchInfo; onClick: () => void; tournamentName?: string }) {
  const winner = match.winnerId === match.teamAId ? match.teamA : match.teamB;
  const setsWonA = match.sets.filter(s => s.winner_id === match.teamAId).length;
  const setsWonB = match.sets.filter(s => s.winner_id === match.teamBId).length;
  const finalScoreA = match.sets.reduce((sum, s) => sum + (s.score_a ?? 0), 0);
  const finalScoreB = match.sets.reduce((sum, s) => sum + (s.score_b ?? 0), 0);
  const winnerIsA = match.winnerId === match.teamAId;
  const winnerIsB = match.winnerId === match.teamBId;
  const maxSets = match.matchType === 'playoff' ? 5 : 3;

  return (
    <div
      onClick={onClick}
      className="w-full bg-surface border border-border rounded-xl p-4 hover:border-text-muted transition-colors text-left cursor-pointer"
    >
      {/* Match Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-text-secondary">MATCH #{match.matchNumber}</span>
          {tournamentName && <span className="text-[10px] text-text-secondary hidden sm:inline">{tournamentName}</span>}
        </div>
        <div className="flex items-center gap-2">
          {match.matchType === 'playoff' && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-warning/20 text-warning">PLAYOFF</span>
          )}
          {match.matchType === 'league' && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-info/20 text-info">LEAGUE</span>
          )}
        </div>
      </div>

      {/* Teams + Score */}
      <div className="flex items-center justify-between gap-2 overflow-hidden">
        {/* Team A */}
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <div className="flex flex-col items-center gap-1 min-w-[60px] flex-shrink-0">
            <TeamLogo team={(match.teamA ?? { id: '', name: '?', shortName: '?', color: '#64748B', players: [] }) as any} size="md" className={winnerIsA ? '' : 'opacity-50'} />
            <span className="text-xs font-bold" style={{ color: match.teamA?.color }}>{match.teamA?.shortName ?? '—'}</span>
          </div>
          <div className="text-center">
            <p className={`font-mono-nums text-4xl font-bold ${winnerIsA ? 'text-text-primary' : 'text-text-muted'}`}>{finalScoreA}</p>
            <div className="flex gap-1 justify-center mt-1">
              {[...Array(maxSets)].map((_, i) => {
                const wonSet = match.sets.find(s => s.set_number === i + 1)?.winner_id === match.teamAId;
                return (
                  <span key={i} className={`w-2 h-2 rounded-full ${wonSet ? '' : 'bg-border'}`}
                    style={wonSet ? { backgroundColor: match.teamA?.color } : undefined} />
                );
              })}
            </div>
          </div>
        </div>

        {/* FT / Sets */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <span className="text-[10px] font-bold text-text-secondary">FT</span>
          <span className="text-lg font-bold text-text-secondary">{setsWonA} - {setsWonB}</span>
          <span className="text-[10px] text-text-secondary">SETS</span>
        </div>

        {/* Team B */}
        <div className="flex-1 flex items-center gap-2 flex-row-reverse min-w-0">
          <div className="flex flex-col items-center gap-1 min-w-[60px] flex-shrink-0">
            <TeamLogo team={(match.teamB ?? { id: '', name: '?', shortName: '?', color: '#64748B', players: [] }) as any} size="md" className={winnerIsB ? '' : 'opacity-50'} />
            <span className="text-xs font-bold" style={{ color: match.teamB?.color }}>{match.teamB?.shortName ?? '—'}</span>
          </div>
          <div className="text-center">
            <p className={`font-mono-nums text-4xl font-bold ${winnerIsB ? 'text-text-primary' : 'text-text-muted'}`}>{finalScoreB}</p>
            <div className="flex gap-1 justify-center mt-1">
              {[...Array(maxSets)].map((_, i) => {
                const wonSet = match.sets.find(s => s.set_number === i + 1)?.winner_id === match.teamBId;
                return (
                  <span key={i} className={`w-2 h-2 rounded-full ${wonSet ? '' : 'bg-border'}`}
                    style={wonSet ? { backgroundColor: match.teamB?.color } : undefined} />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-xs">
        <span className="text-text-secondary font-medium">
          {winner && (
            <span className="flex items-center gap-1">
              🏆 <span style={{ color: winner.color }}>{winner.name}</span>
            </span>
          )}
        </span>
        <span className="text-text-secondary">
          {formatMatchDate(match.date)}{match.matchTime ? ` ${formatMatchTime(match.matchTime)}` : ''}
        </span>
      </div>
      {match.liveStreamUrl && (
        <WatchLiveButton isCompleted onClick={(e) => e.stopPropagation()} url={match.liveStreamUrl} />
      )}
    </div>
  );
}

// Points Table Tab - Card-per-team layout (no alignment issues)
function PointsTableTab({ rows }: { rows: PointsTableRow[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3">🏆</div>
        <p className="text-text-secondary text-sm">No standings yet</p>
        <p className="text-text-muted text-xs mt-1">Matches need to be completed first</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const isExpanded = expandedId === row.teamId;

        return (
          <div key={row.teamId} className="bg-surface border border-border rounded-xl overflow-hidden">
            <button
              onClick={() => setExpandedId(isExpanded ? null : row.teamId)}
              className="w-full px-3 py-3 flex items-center gap-2 hover:bg-surface-raised transition-colors"
            >
              {/* Team logo + name — flex-1 so name gets all remaining space */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {row.teamLogoUrl ? (
                  <img src={row.teamLogoUrl} alt={row.teamShortName}
                    loading="lazy"
                    className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                ) : (
                  <span className="w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: row.teamColor }} />
                )}
                <span className="font-bold text-sm leading-tight" style={{ color: row.teamColor }}>
                  {row.teamName}
                </span>
              </div>

              {/* P / W / L / PTS — collapsed view */}
              <div className="flex items-center gap-2 text-xs flex-shrink-0">
                <div className="text-center w-7">
                  <p className="font-bold text-sky-400">{row.played}</p>
                  <p className="text-text-secondary text-[10px] font-medium">P</p>
                </div>
                <div className="text-center w-7">
                  <p className="font-bold text-success">{row.wins}</p>
                  <p className="text-text-secondary text-[10px] font-medium">W</p>
                </div>
                <div className="text-center w-7">
                  <p className="font-bold text-error">{row.losses}</p>
                  <p className="text-text-secondary text-[10px] font-medium">L</p>
                </div>
                <div className="text-center w-9">
                  <p className="font-bold text-primary">{row.points}</p>
                  <p className="text-text-secondary text-[10px] font-medium">PTS</p>
                </div>
              </div>

              {/* Expand arrow */}
              <svg className={`w-4 h-4 text-text-muted flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {/* Expanded details */}
            {isExpanded && (() => {
              const setNet = row.setsWon - row.setsLost;
              const totalSets = row.setsWon + row.setsLost;
              const pointsNet = row.netPoints;
              const totalPoints = row.pointsScored + row.pointsConceded;
              const pointsPct = totalPoints > 0 ? (row.pointsScored / totalPoints * 100).toFixed(0) : '0';
              return (
                <div className="px-4 pb-4 pt-0 border-t border-border bg-surface-raised/50">
                  {/* Row 1: Sets */}
                  <div className="grid grid-cols-4 gap-3 pt-3">
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase">Sets Won</p>
                      <p className="text-sm font-bold text-success">{row.setsWon}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase">Sets Lost</p>
                      <p className="text-sm font-bold text-error">{row.setsLost}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase">Set Net</p>
                      <p className={`text-sm font-bold ${setNet >= 0 ? 'text-success' : 'text-error'}`}>
                        {setNet >= 0 ? `+${setNet}` : setNet}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase">SET%</p>
                      <p className="text-sm font-bold text-text-primary">{totalSets > 0 ? row.setWinPct.toFixed(0) : '0'}%</p>
                    </div>
                  </div>
                  {/* Row 2: Points */}
                  <div className="grid grid-cols-4 gap-3 pt-3">
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase">Pts Won</p>
                      <p className="text-sm font-bold text-success">{row.pointsScored}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase">Pts Lost</p>
                      <p className="text-sm font-bold text-error">{row.pointsConceded}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase">Pts Net</p>
                      <p className={`text-sm font-bold ${pointsNet >= 0 ? 'text-success' : 'text-error'}`}>
                        {pointsNet >= 0 ? `+${pointsNet}` : pointsNet}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase">PTS%</p>
                      <p className="text-sm font-bold text-text-primary">{pointsPct}%</p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

// Player Stats Tab - Category-based leaderboards
function PlayerStatsTab({
  players,
  teams,
  playerSearch,
  setPlayerSearch,
  teamFilter,
  setTeamFilter,
  playerCategory,
  setPlayerCategory,
  navigate,
}: {
  players: PlayerStarRow[];
  teams: TeamInfo[];
  playerSearch: string;
  setPlayerSearch: (v: string) => void;
  teamFilter: string;
  setTeamFilter: (v: string) => void;
  playerCategory: PlayerCategory;
  setPlayerCategory: (v: PlayerCategory) => void;
  navigate: (path: string) => void;
}) {
  const sortedPlayers = useMemo(() => {
    switch (playerCategory) {
      case 'thrower':
        return [...players].sort((a, b) => b.throwerStars - a.throwerStars);
      case 'acer':
        return [...players].sort((a, b) => b.acerStars - a.acerStars);
      case 'catcher':
        return [...players].sort((a, b) => b.catcherStars - a.catcherStars);
      case 'mvp':
      default:
        return [...players].sort((a, b) => b.totalStars - a.totalStars);
    }
  }, [players, playerCategory]);

  const getPoints = (p: PlayerStarRow) => {
    switch (playerCategory) {
      case 'thrower': return p.throwerStars;
      case 'acer': return p.acerStars;
      case 'catcher': return p.catcherStars;
      case 'mvp': default: return p.totalStars;
    }
  };

  return (
    <div className="space-y-4">
      {/* Category Tabs — first, per vb-app order */}
      <div className="flex gap-1 overflow-x-auto scrollbar-hide pb-2">
        <PlayerCategoryTab icon="🌟" label="MVP" active={playerCategory === 'mvp'} onClick={() => setPlayerCategory('mvp')} />
        <PlayerCategoryTab icon="🎯" label="Thrower" active={playerCategory === 'thrower'} onClick={() => setPlayerCategory('thrower')} />
        <PlayerCategoryTab icon="⚡" label="Acer" active={playerCategory === 'acer'} onClick={() => setPlayerCategory('acer')} />
        <PlayerCategoryTab icon="🧤" label="Catcher" active={playerCategory === 'catcher'} onClick={() => setPlayerCategory('catcher')} />
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Search player..."
          value={playerSearch}
          onChange={e => setPlayerSearch(e.target.value)}
          className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        <select
          value={teamFilter}
          onChange={e => setTeamFilter(e.target.value)}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="all">All Teams</option>
          {teams.map(t => (
            <option key={t.id} value={t.id}>{t.shortName}</option>
          ))}
        </select>
      </div>

      {sortedPlayers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-text-secondary text-sm">No players found</p>
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          {sortedPlayers.map((p, i) => {
            const points = getPoints(p);
            const isTopThree = i < 3;
            return (
              <div
                key={p.playerId}
                className={`flex items-center gap-3 px-3 py-2.5 ${
                  i !== sortedPlayers.length - 1 ? 'border-b border-border' : ''
                } ${isTopThree ? 'bg-warning/5' : ''}`}
              >
                {/* Rank */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                  i === 0 ? 'bg-warning text-white' :
                  i === 1 ? 'bg-gray-400 text-white' :
                  i === 2 ? 'bg-orange-400 text-white' :
                  'bg-surface-raised text-text-secondary'
                }`}>
                  {i + 1}
                </div>

                {/* Player + Team */}
                <div className="min-w-0 flex-1 flex items-center justify-between gap-2">
                  <button
                    onClick={() => navigate(`/player/${slugifyPlayerName(p.playerName)}`)}
                    className="font-bold text-sm text-text-primary truncate hover:underline text-left"
                  >
                    {p.playerName}
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {p.teamLogoUrl ? (
                      <img src={p.teamLogoUrl} alt={p.teamShortName} loading="lazy" className="w-4 h-4 rounded-full object-contain flex-shrink-0" />
                    ) : (
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.teamColor }} />
                    )}
                    <span className="text-xs font-semibold text-text-secondary">{p.teamShortName}</span>
                  </div>
                </div>

                {/* Points */}
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-lg text-text-primary">{points}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sortedPlayers.length > 50 && (
        <p className="text-center text-[10px] text-text-muted">Showing top 50 players</p>
      )}
    </div>
  );
}

function PlayerCategoryTab({ icon, label, active, onClick }: { icon: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-bold uppercase transition-colors ${
        active
          ? 'bg-surface border-b-2 border-info text-info font-bold'
          : 'text-text-secondary hover:text-text-primary'
      }`}
    >
      {icon} {label}
    </button>
  );
}

// Tournament Stats Tab - Cards
function TournamentStatsTab({
  stats,
  totalRallies,
  totalSets,
  totalPoints: _totalPoints,
  duration,
  matches,
  teams,
  pointsRows,
  playerRows,
}: {
  stats: { total: number; completed: number; live: number; pending: number; scheduled: number };
  totalRallies: number;
  totalSets: number;
  totalPoints: number;
  duration: string | null;
  matches: MatchInfo[];
  teams: TeamInfo[];
  pointsRows: PointsTableRow[];
  playerRows: PlayerStarRow[];
}) {
  const topTeam = pointsRows.length > 0 ? pointsRows[0] : null;
  const topPlayer = playerRows.length > 0
    ? [...playerRows].sort((a, b) => b.totalStars - a.totalStars)[0]
    : null;
  const totalPlayers = teams.reduce((sum, t) => sum + (t.players?.length ?? 0), 0);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <StatCard label="Matches" value={stats.total} />
      <StatCard label="Completed" value={stats.completed} accent="completed" />
      <StatCard label="Live" value={stats.live} accent="live" />
      <StatCard label="Upcoming" value={stats.scheduled} accent="upcoming" />
      <StatCard label="Players" value={totalPlayers} />
      <StatCard label="Teams" value={teams.length} />
      <StatCard label="Total Sets" value={totalSets} />
      <StatCard label="Total Rallies" value={totalRallies} />
      <StatCard label="Duration" value={duration || '—'} />

      {/* Top Team */}
      {topTeam && (
        <div className="bg-surface border border-border rounded-xl p-4 col-span-2 sm:col-span-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Top Team</p>
          <div className="flex items-center gap-3">
            {topTeam.teamLogoUrl ? (
              <img src={topTeam.teamLogoUrl} alt={topTeam.teamShortName} loading="lazy" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <span className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: topTeam.teamColor }} />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-base font-bold truncate" style={{ color: topTeam.teamColor }}>{topTeam.teamShortName}</p>
              <p className="text-xs text-text-secondary">{topTeam.points} points · {topTeam.wins}W {topTeam.losses}L</p>
            </div>
            <span className="ml-auto text-2xl flex-shrink-0">🥇</span>
          </div>
        </div>
      )}

      {/* Top Player */}
      {topPlayer && (
        <div className="bg-surface border border-border rounded-xl p-4 col-span-2 sm:col-span-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Top Player</p>
          <div className="flex items-center gap-3">
            {topPlayer.teamLogoUrl ? (
              <img src={topPlayer.teamLogoUrl} alt={topPlayer.teamShortName} loading="lazy" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <span className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: topPlayer.teamColor }} />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-base font-bold truncate" style={{ color: topPlayer.teamColor }}>{topPlayer.playerName}</p>
              <p className="text-xs text-text-secondary">{topPlayer.totalStars} stars · {topPlayer.teamShortName}</p>
            </div>
            <span className="ml-auto text-2xl flex-shrink-0">🌟</span>
          </div>
        </div>
      )}

      {/* Participating Teams */}
      <div className="bg-surface border border-border rounded-xl p-4 col-span-2 sm:col-span-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-3">Participating Teams ({teams.length})</p>
        <div className="flex flex-wrap gap-2">
          {teams.map(team => (
            <div
              key={team.id}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-background border border-border"
              style={{ borderLeftWidth: 3, borderLeftColor: team.color }}
            >
              <TeamLogo team={team as any} size="xs" />
              <span className="text-xs font-semibold" style={{ color: team.color }}>{team.shortName}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Matches */}
      {matches.length > 0 && (
        <div className="bg-surface border border-border rounded-xl p-4 col-span-2 sm:col-span-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-3">Recent Matches</p>
          <div className="space-y-2">
            {[...matches].sort((a, b) => b.matchNumber - a.matchNumber).slice(0, 8).map(m => {
              const winner = m.winnerId ? (m.winnerId === m.teamAId ? m.teamA : m.teamB) : null;
              const isLive = m.status === 'live' || m.status === 'set-won';
              const isTimeout = m.status === 'timeout';
              const isScheduledStatus = m.status === 'scheduled';
              const isCompleted = m.status === 'match-won';
              const displayDate = m.date
                ? new Date(m.date + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                : null;
              return (
                <div key={m.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-[10px] text-text-muted flex-shrink-0">#{m.matchNumber}</span>
                    <span className="text-xs font-semibold truncate" style={{ color: m.teamA?.color }}>{m.teamA?.shortName}</span>
                    <span className="text-[10px] text-text-secondary">vs</span>
                    <span className="text-xs font-semibold truncate" style={{ color: m.teamB?.color }}>{m.teamB?.shortName}</span>
                    {displayDate && <span className="text-[10px] text-text-muted flex-shrink-0">{displayDate}</span>}
                  </div>
                  {isLive && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-warning/10 text-warning flex-shrink-0">LIVE</span>}
                  {isTimeout && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 flex-shrink-0">TIMEOUT</span>}
                  {isScheduledStatus && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-info/10 text-info flex-shrink-0">SCHEDULED</span>}
                  {isCompleted && winner && <span className="text-[10px] font-semibold text-success flex-shrink-0">{winner.shortName}</span>}
                </div>
              );
            })}
          </div>
          {matches.length > 8 && (
            <p className="text-center text-[10px] text-text-muted mt-2">+{matches.length - 8} more matches</p>
          )}
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, accent }: { label: string; value: string | number; accent?: 'completed' | 'live' | 'upcoming' }) {
  const accentClass =
    accent === 'completed' ? 'text-success' :
    accent === 'live' ? 'text-warning' :
    accent === 'upcoming' ? 'text-text-muted' :
    'text-text-primary';

  return (
    <div className="bg-surface border border-border rounded-xl p-4 text-center">
      <p className={`text-3xl font-bold ${accentClass}`}>{value}</p>
      <p className="text-xs text-text-secondary font-medium mt-1">{label}</p>
    </div>
  );
}
